import { useEffect, useRef, useState } from "react";
import { useDataProvider } from "react-admin";
import {
  ResponsiveContainer, BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";
import {
  Typography,
  Box,
  Stack,
  TextField as MuiTextField,
} from "@mui/material";

interface Record {
  date: string;
  fullBins: number;
  emptyBins: number;
}

const formatDate = (d: Date) => d.toISOString().split("T")[0];

const InventoryGraph = ({ providerId }: { providerId?: string }) => {
  const dataProvider = useDataProvider();
  const [data, setData] = useState<Record[]>([]);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const defaultEnd = formatDate(now);
  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);
  const defaultStart = formatDate(oneMonthAgo);

  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);

  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    const operands: any[] = [];
    if (providerId) {
      operands.push({
        term: { name: "provider", filterType: "eq", value: providerId },
      });
    }
    if (startDate) {
      operands.push({
        term: {
          name: "createdAt",
          filterType: "gte",
          value: new Date(startDate).toISOString(),
        },
      });
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      operands.push({
        term: {
          name: "createdAt",
          filterType: "lte",
          value: end.toISOString(),
        },
      });
    }

    const filter = {
      filterTree: {
        operator: "and",
        operands,
      },
    };

    dataProvider
      .getList("provider-inventory", {
        pagination: { page: 1, perPage: -1 },
        sort: { field: "createdAt", order: "ASC" },
        filter,
      })
      .then(({ data }) => {
        setData(
          data.map((rec: any) => ({
            date: new Date(rec.createdAt).toLocaleDateString("fr-FR"),
            fullBins: rec.fullBinsCount,
            emptyBins: rec.emptyBinsCount,
          }))
        );
      })
      .finally(() => setLoading(false));
  }, [dataProvider, providerId, startDate, endDate]);

  if (loading) return <div>Loading chart...</div>;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <MuiTextField
          label="Date de début"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          inputRef={startRef}
          onClick={() => startRef.current?.showPicker?.()}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <MuiTextField
          label="Date de fin"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          inputRef={endRef}
          onClick={() => endRef.current?.showPicker?.()}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Stack>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Caisses pleines et vides
      </Typography>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend verticalAlign="top" height={36} />
          <Bar
            dataKey="fullBins"
            name="Caisses pleines"
            barSize={20}
            fill="#82ca9d"
          />
          <Bar
            dataKey="emptyBins"
            name="Caisses vides"
            barSize={20}
            fill="#ffc658"
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default InventoryGraph;
